import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { toAudString } from 'utils/currency';
import { ReactComponent as SeatIcon } from 'assets/icons/svgs/icon-seat-2.svg';
import { Link } from 'components/UI/Link/Link';
import { asPerformanceDate, asTimeISO } from 'utils/date-formatter';
import { RemoveSeatsPrompt } from './Prompts/RemoveSeatsPrompt';

/**
 * Render Upsell Items
 * @private
 * @param {Object} event
 * @returns
 */
const renderUpsell = (event, removeHandler) => (
  <table className="table-css-responsive table-fullwidth cart-table cart-table-payment">
    <thead>
      <tr>
        <th>Add ons</th>
        <th className="cell-price">Price</th>
      </tr>
    </thead>
    <tbody>
      {event.TicketedItems.map((upsellTicket) => (
        <Fragment key={upsellTicket.LineItemId}>
          {upsellTicket.SubLineItems.map((upsell) => (
            <>
              <tr className="row-cart-addons">
                <td className="seat-info">{upsell.SeatTypeDescription}</td>
                <td className="addon-price seat-info">{toAudString(upsell.AmountDue)}</td>
              </tr>
              <tr>
                <td colSpan="2" className="seat-info cell-remove">
                  <Link
                    href="#"
                    onClick={() => {
                      removeHandler(event);
                    }}
                    extendClass="link-bold cart-remove"
                    variant="secondary"
                  >
                    Remove {upsell.SeatTypeDescription}
                  </Link>
                </td>
              </tr>
            </>
          ))}
        </Fragment>
      ))}
    </tbody>
  </table>
);

/**
 * Render seat rows
 * @param {*} event
 * @returns
 */
const renderSeats = (event, removeHandler) => (
  <table className="table-css-responsive table-fullwidth cart-table cart-table-payment">
    <thead>
      <tr>
        <th>Seat</th>
        <th>Section</th>
        <th>Zone</th>
        <th>Type</th>
        {event.HasDiscountedTickets && <th className="cell-price">Original price</th>}
        <th className="cell-price" colSpan={event.HasDiscountedTickets ? 1 : 2}>
          Price
        </th>
      </tr>
    </thead>
    <tbody>
      {event.TicketedItems.sort((n) => n.IsSeated).map((ticket) => (
        <Fragment key={ticket.LineItemId}>
          {ticket.SubLineItems.map((seat) => (
            <tr key={seat.SeatId} className="row-seat-info">
              <td className="seat-info">{ticket.IsSeated ? seat.SeatRow + seat.SeatNumber : ''}</td>
              <td className="seat-info">{seat.SectionShortDescription}</td>
              <td className="seat-info">{seat.ZoneDescription}</td>
              {seat.SeatTypeAliasDescription ? (
                <td className="seat-info">{seat.SeatTypeAliasDescription}</td>
              ) : (
                <td className="seat-info">{seat.SeatTypeDescription}</td>
              )}
              {event.HasDiscountedTickets && (
                <td className="seat-info cell-price">
                  {seat.OrignalAmount > 0 && seat.OrignalAmount > seat.AmountDue ? (
                    <span className="price-old">{toAudString(seat.OrignalAmount)}</span>
                  ) : (
                    <span />
                  )}
                </td>
              )}
              <td className="seat-info cell-price" colSpan={event.HasDiscountedTickets ? 1 : 2}>
                {toAudString(seat.AmountDue)}
              </td>
            </tr>
          ))}
          <tr>
            {ticket.ShowChangeSeats && (
              <td colSpan={event.HasDiscountedTickets ? 6 : 5}>
                <Link
                  startIcon={<SeatIcon />}
                  href={ticket.ChangeSeatsURL}
                  extendClass="link-bold cart-remove change-seat-link"
                  variant="secondary"
                >
                  Change your seats?
                </Link>
              </td>
            )}
            <td className="seat-info cell-remove" colSpan={event.HasDiscountedTickets ? 6 : 5}>
              <Link
                href="#"
                onClick={() => {
                  removeHandler({ ...ticket, EventUrl: event?.EventUrl });
                }}
                extendClass="link-bold cart-remove"
                variant="secondary"
              >
                Remove seats
              </Link>
            </td>
          </tr>
        </Fragment>
      ))}
    </tbody>
  </table>
);

/**
 * Render Tickets
 * @param {object} props
 * @returns
 */
export const Tickets = (props) => {
  const { items, stepName } = props;
  const [removeEvent, setRemoveEvent] = useState(null);

  const showPrompt = (event) => {
    setRemoveEvent(event);
  };

  const handleRequestClose = () => {
    setRemoveEvent(null);
  };

  return (
    <>
      <RemoveSeatsPrompt
        active={!!removeEvent}
        event={removeEvent}
        onRequestClose={handleRequestClose}
        stepName={stepName}
      />

      {items.map((event) => (
        <div key={event.PerformanceId}>
          <div className="row">
            <div className="col-md-7">
              <div className="cart-show-preview">
                <div className="pull-left show-img-thumb hidden-xs">
                  {event.ImageUrl && (
                    <img
                      src={event.ImageUrl}
                      alt={event.PerformanceDescription}
                      width="147"
                      height="83"
                    />
                  )}
                </div>
                <div className="pull-left show-info">
                  <p>
                    <strong className="show-name">{event.PerformanceDescription}</strong>
                  </p>
                  <span className="show-venue">{event.Venue}</span>
                  <br />
                  {event.ShowDateOnly && (
                    <strong className="show-time">
                      {asPerformanceDate(event.IsoPerformanceDate)}
                    </strong>
                  )}

                  {event.ShowDateTime && (
                    <strong className="show-time">
                      {asPerformanceDate(event.IsoPerformanceDate)} at {asTimeISO(event.IsoPerformanceDate)}
                    </strong>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/** I think this is a bug from Angular and should be IsUpsellItem??
           * Dosnt look like it ever displays. Leaving as Angular has for now. */}
          {event.UpsellItem ? renderUpsell(event, showPrompt) : renderSeats(event, showPrompt)}
          <hr className="hr-double" />
        </div>
      ))}
    </>
  );
};

Tickets.propTypes = {
  items: PropTypes.array.isRequired,
  stepName: PropTypes.string,
};
